import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { useAssignableUsers } from '../users/queries';
import { EmailCombobox } from '../crm/EmailCombobox';
import { useSession } from '../auth/queries';
import { reportTitle } from './presentation';
import {
  useCreateReportSchedule,
  useDeleteReportSchedule,
  useReportCatalog,
  useReportSchedules,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Geplanter Berichtsversand -- F5-09, K-125.
 *
 * **Der Bericht steht im Rumpf der Mail, nicht im Anhang.** Wer montags eine
 * Auswertung bekommt, will die Zahlen sehen und nicht erst eine Datei
 * oeffnen -- auf dem Telefon oeffnet er sie gar nicht.
 *
 * **Jeder Empfaenger bekommt seine eigene Nachricht.** Ein Verteiler im
 * ``To``-Feld verriete jedem, wer den Bericht sonst noch bekommt; bei einer
 * Auswertung ueber die Arbeit von Kollegen ist das eine Angabe, die niemand
 * verlangt hat. Die Maske sagt es, damit niemand einen Verteiler erwartet.
 *
 * **Der Zeitraum ist relativ.** "Die letzten sieben Tage", vom Lauf aus
 * gerechnet -- ein fester Zeitraum ergaebe jede Woche dieselben Zahlen.
 */
export function SchedulePanel() {
  const { t } = useTranslation();
  const session = useSession();
  const catalog = useReportCatalog();
  const schedules = useReportSchedules();
  const create = useCreateReportSchedule();
  const remove = useDeleteReportSchedule();

  const canConfigure = (session.data?.permissions ?? []).includes('masterdata.write');

  const [name, setName] = useState('');
  const [reportKey, setReportKey] = useState('ticket_volume');
  const [periodDays, setPeriodDays] = useState('7');
  const [cron, setCron] = useState('0 8 * * 1');
  const [recipients, setRecipients] = useState<string[]>([]);
  // Was im Feld steht, aber noch nicht mit der Eingabetaste uebernommen ist.
  // Es zaehlt trotzdem: Wer eine Adresse tippt und "Anlegen" klickt, hat sie
  // gemeint.
  const [pending, setPending] = useState('');
  const users = useAssignableUsers();

  const parsedRecipients = [...recipients, ...(pending !== '' ? [pending] : [])].filter(
    (entry, index, all) =>
      all.findIndex((other) => other.toLowerCase() === entry.toLowerCase()) === index,
  );

  // Bearbeiter stehen mit in der Auswahl: Ein Bericht geht oefter an die
  // eigene Leitung als an einen Kunden.
  const userOptions = (users.data ?? [])
    .filter((user) => user.is_active && user.email)
    .map((user) => ({ id: user.email, label: user.full_name, hint: user.email }));

  return (
    <div className="stack">
      <section className="card card--flush">
        {schedules.isPending ? <SkeletonTable columns={5} /> : null}
        {schedules.isError ? <ErrorNotice error={schedules.error} /> : null}

        {schedules.data?.length === 0 ? (
          <EmptyState icon="clock" title={t('schedules.empty')} hint={t('schedules.emptyHint')} />
        ) : null}

        {schedules.data && schedules.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('schedules.report')}</th>
                  <th scope="col">{t('schedules.when')}</th>
                  <th scope="col">{t('schedules.recipients')}</th>
                  {canConfigure ? <th scope="col">{t('tickets.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {schedules.data.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>
                      {schedule.name}{' '}
                      {schedule.is_active ? null : (
                        <Badge tone="neutral">{t('masterdata.inactive')}</Badge>
                      )}
                    </td>
                    <td className="table__muted">
                      {(() => {
                        const entry = catalog.data?.find(
                          (item) => item.key === schedule.report_key,
                        );
                        return entry ? reportTitle(t, entry) : schedule.report_key;
                      })()}
                      {' · '}
                      {t('schedules.lastDays', { count: schedule.period_days })}
                    </td>
                    <td className="table__muted">
                      <code>{schedule.cron_expression}</code>
                    </td>
                    <td className="table__muted">{schedule.recipients.join(', ')}</td>
                    {canConfigure ? (
                      <td>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(schedule.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {canConfigure ? (
        <section className="card">
          <h2>{t('schedules.new')}</h2>
          <p className="table__muted">{t('schedules.hint')}</p>

          <div className="field">
            <label className="field__label" htmlFor="schedule-name">
              {t('masterdata.name')}
            </label>
            <input
              id="schedule-name"
              className="field__control"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>

          <div className="row">
            <div className="field">
              <label className="field__label" htmlFor="schedule-report">
                {t('schedules.report')}
              </label>
              <Select
                id="schedule-report"
                className="field__control"
                value={reportKey}
                onChange={(event) => {
                  setReportKey(event.target.value);
                }}
              >
                {(catalog.data ?? []).map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {reportTitle(t, entry)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="schedule-period">
                {t('schedules.period')}
              </label>
              <input
                id="schedule-period"
                type="number"
                min={1}
                max={366}
                className="field__control"
                value={periodDays}
                onChange={(event) => {
                  setPeriodDays(event.target.value);
                }}
              />
              <span className="field__hint">{t('schedules.periodHint')}</span>
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="schedule-cron">
              {t('schedules.when')}
            </label>
            <input
              id="schedule-cron"
              className="field__control"
              value={cron}
              onChange={(event) => {
                setCron(event.target.value);
              }}
            />
            <span className="field__hint">{t('schedules.cronHint')}</span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="schedule-recipients">
              {t('schedules.recipients')}
            </label>
            {recipients.length > 0 ? (
              <div className="kb-chips">
                {recipients.map((address) => (
                  <button
                    key={address}
                    type="button"
                    className="kb-chip"
                    aria-label={t('schedules.removeRecipient', { address })}
                    onClick={() => {
                      setRecipients((current) => current.filter((entry) => entry !== address));
                    }}
                  >
                    {address}
                    <span className="kb-chip__remove" aria-hidden="true">
                      ×
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            <EmailCombobox
              id="schedule-recipients"
              value=""
              keepValue={false}
              extraOptions={userOptions}
              excludeEmails={recipients}
              onChange={(address) => {
                if (address === '') return;
                setRecipients((current) =>
                  current.some((entry) => entry.toLowerCase() === address.toLowerCase())
                    ? current
                    : [...current, address],
                );
              }}
              onPending={setPending}
            />
            <span className="field__hint">{t('schedules.recipientsHint')}</span>
          </div>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            disabled={!name.trim() || parsedRecipients.length === 0}
            busy={create.isPending}
            onClick={() => {
              create.mutate(
                {
                  name: name.trim(),
                  report_key: reportKey,
                  period_days: Number(periodDays),
                  cron_expression: cron.trim(),
                  recipients: parsedRecipients,
                },
                {
                  onSuccess: () => {
                    setName('');
                    setRecipients([]);
                    setPending('');
                  },
                },
              );
            }}
          >
            {t('masterdata.add')}
          </Button>
        </section>
      ) : null}
    </div>
  );
}
